import { getJSONSchemaURL } from '@y42/feature/json-schema';
import { ColumnTypeEnum, OperationModel } from '@y42/json-schema';
import type { DataTestable } from './data-testable.model';

export class DataTestGitModel {
  constructor(model: DataTestable) {
    this.$schema = getJSONSchemaURL('data-test-settings');
    this.tests = model.tests?.tests ?? [];
  }
  public $schema: string;
  public tests: DataTest[];
}

export type DataTest =
  | SchemaTestForm
  | RowDriftTestForm
  | ColumnTestForm
  | LowCodeTestForm
  | ReferentialIntegrityTestForm
  | UniqueTestForm;

export type DataTestResult =
  | SchemaTestResult
  | RowDriftTestResult
  | ColumnTestResult
  | LowCodeTestResult
  | ReferentialIntegrityTestResult
  | UniqueTestResult;

export interface SchemaTestForm {
  type: DataTestType.Schema;
  allow_new_columns: boolean;
  schema: Array<{ column: string; type: ColumnTypeEnum; allow_null_values: boolean; required?: boolean }>;
}

export type SchemaTestResult = BaseTestResults;

export interface RowDriftTestForm {
  type: DataTestType.NumRowDrift;
  row_change: {
    threshold_type: 'absolute' | 'relative';
    lower_threshold: number;
    upper_threshold: number;
  };
}

export type RowDriftTestResult = BaseTestResults;

export interface ColumnTestForm {
  type: DataTestType.Column;
  column: string;
  column_conditions: {
    operations: OperationModel[];
    rows_failed_condition?: 'absolute' | 'relative';
    rows_failed_by_condition?: number;
  };
}

export type ColumnTestResult = BaseTestResults;

export interface LowCodeTestForm {
  type: DataTestType.LowCode;
  low_code_string: string; // e.g. "@age > 10",
  rows_failed_condition?: 'absolute' | 'relative';
  rows_failed_by_condition?: number;
}

export type LowCodeTestResult = BaseTestResults;

export interface ReferentialIntegrityTestForm {
  type: DataTestType.ReferentialIntegrity;
  column: string;
  foreign_table_path: string;
  foreign_table_column: string;
  allow_null_values: boolean;
  rows_failed_condition?: 'absolute' | 'relative';
  rows_failed_by_condition?: number;
}

export type ReferentialIntegrityTestResult = BaseTestResults;

export interface UniqueTestForm {
  type: DataTestType.Unique;
  column: string;
  allow_null_values: boolean;
}

export type UniqueTestResult = BaseTestResults;

export enum DataTestType {
  Schema = 'schema',
  NumRowDrift = 'num_row_drift',
  Column = 'column',
  LowCode = 'low_code',
  Unique = 'unique',
  ReferentialIntegrity = 'referential_integrity',
}

export enum TestResultStatus {
  Success = 'success',
  Error = 'error',
}

export interface BaseTestResults {
  status: TestResultStatus;
  error_messages: string[];
  num_rows_failed?: number;
  num_columns_failed?: number;
  start_time: string;
  end_time: string;
}

export type RunTestResponseUIModel = RunTestResponseHttpModel['result'];

export interface RunTestRequestHttpModel {
  config: DataTest;
}

export interface RunTestResponseHttpModel {
  result: DataTestResult;
  status: 'ok' | 'error';
}

export interface DataTestRunnerState {
  runTest: {
    loading: boolean;
    response: RunTestResponseUIModel | null;
    httpErrorOccurred: boolean;
  };
  rerunTestSuite: {
    loading: boolean;
    httpErrorOccurred: boolean;
  };
}
